/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { HtmlHeadInclude, ICoreContext } from '@msdyn365-commerce/core';

import { IDlvryCompanyChainContainerData } from './dlvry-company-chain-container.data';
import { CompanyChainMatchesMatchType, ICompanyChainMatchesData, IDlvryCompanyChainContainerProps as IDlvryCompanyChainContainerPropsAuto } from './dlvry-company-chain-container.props.autogenerated';
import { IAppSettingsInContext } from '../../settings/app.settings.autogenerated';

export interface IDlvryCompanyChainContainerViewProps extends IDlvryCompanyChainContainerPropsAuto<IDlvryCompanyChainContainerData> {}

export interface IDlvryCompanyChainContainerProps extends IDlvryCompanyChainContainerPropsAuto<IDlvryCompanyChainContainerData> {
    context: ICoreContext<IAppSettingsInContext>;
}

function getMatchFunction(matchType: CompanyChainMatchesMatchType): (companyChain: string, matchValue: string) => boolean {
    switch (matchType) {
        case CompanyChainMatchesMatchType.exact:
            return (companyChain, matchValue) => companyChain === matchValue;
        case CompanyChainMatchesMatchType.startsWith:
            return (companyChain, matchValue) => companyChain.startsWith(matchValue);
        case CompanyChainMatchesMatchType.endsWith:
            return (companyChain, matchValue) => companyChain.endsWith(matchValue);
        case CompanyChainMatchesMatchType.contains:
            return (companyChain, matchValue) => companyChain.includes(matchValue);
        default:
            return () => false;
    }
}

/**
 *
 * DlvryCompanyChainContainer component
 * @extends {React.PureComponent<IDlvryCompanyChainContainerProps>}
 */
class DlvryCompanyChainContainer extends React.PureComponent<IDlvryCompanyChainContainerProps> {

    private readonly companyChainIdPropertyName = 'COMPANYCHAINID';

    private getMatches(companyChain: string): ICompanyChainMatchesData[] {
        const { companyChainMatches } = this.props.config;

        if (!companyChainMatches || companyChainMatches.length === 0) {
            return [];
        }

        return companyChainMatches.filter(matchConfig => {
            const { matchType, matchValue } = matchConfig;
            return getMatchFunction(matchType)(companyChain, matchValue);
        });
    }

    public render(): JSX.Element | null {
        const { context, config, data, slots } = this.props;
        const { isEditor } = context.request.params;

        if (!isEditor && (!data.orgInformation.result || !config.companyChainMatches?.length)) {
            return null;
        }

        const companyChain = data.orgInformation.result?.ExtensionProperties?.find(prop => prop.Key === this.companyChainIdPropertyName)?.Value?.StringValue;
        const matches = companyChain ? this.getMatches(companyChain) : [];

        if (!isEditor && matches.length === 0) {
            return null;
        }

        // Get a list of the class names we should hide based on matches, splitting by comma and trimming whitespace
        const classNamesToHide = matches
            .flatMap(match => match.hideClassNamesIfMatch
                ? match.hideClassNamesIfMatch.split(',').map(className => className.trim())
                : []);

        return (
            <div>
                <HtmlHeadInclude>
                    <style>
                        {classNamesToHide.map(className => `.${className} { display: none; }`).join('\n')}
                    </style>
                </HtmlHeadInclude>
                {slots.content && slots.content.length > 0 && slots.content.map((slot, index) => (
                    <div key={index}>{slot}</div>
                ))}
            </div>
        );
    }
}

export default DlvryCompanyChainContainer;
